import { FormEvent, ReactElement, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import cx from 'clsx';
import { Box } from '@twilio-paste/core/box';
import { Heading } from '@twilio-paste/core/heading';
import { Text } from '@twilio-paste/core/text';
import SearchInput, {
  searchInputTypes,
} from '@twiliointernal/services-common/components/searchInput';

import { routes, trackingDetails } from '../../definitions/constants';
import { copy, constants, types } from './definitions';
import {
  useGetSearchResults,
  useGetQuerySuggestions,
  useSegment,
  useToasterContext,
} from '../../hooks';
import { searchValueActions } from '../../stores/searchValue.slice';
import { searchResultsActions } from '../../stores/searchResults.slice';
import logger from '../../../lib/logger';
import { WelcomeBanner } from './sub';

import styles from './supportSearch.module.css';

const SupportSearch = ({ variant }: types.SupportSearchProps): ReactElement => {
  const location = useLocation();
  const navigate = useNavigate();
  const toaster = useToasterContext();
  const { trackEvent } = useSegment();
  const dispatch = useDispatch();
  const [errorText, updateErrorText] = useState<string>('');
  const [searchValue, updateSearchValue] = useState<string>('');
  const [searchVariant, updateSearchVariant] =
    useState<types.SupportSearchVariant>(variant);
  const { querySuggestionResults, debouncedSearchSuggestionsRef } =
    useGetQuerySuggestions();
  const [formattedSelectedItem, setFormattedSelectedItem] = useState<
    string | undefined
  >(undefined);
  const {
    searchResults,
    loading: isSearchResultsLoading,
    error: searchResultsError,
    updateQueryText,
  } = useGetSearchResults();

  const hasError = !!errorText;
  const isOnSearchPage = location.pathname === routes.deflectionSearchResults;
  const isOnLandingPage = location.pathname === routes.home;

  useEffect(() => {
    if (searchResults) {
      dispatch(searchResultsActions.set(searchResults));
    }

    if (searchVariant !== variant) {
      updateSearchVariant(variant);
    }
  }, [searchResults, searchVariant, variant, dispatch]);

  useEffect(() => {
    if (searchResultsError) {
      logger.error(`${constants.LOG_PREFIX}::${searchResultsError}`);
      toaster.push({
        message: copy.errors.search,
        variant: 'error',
      });
    }
  }, [searchResultsError]);

  const toggleSearchVariant = (): void => {
    if (searchVariant === types.SupportSearchVariant.withIllustration) {
      updateSearchVariant(types.SupportSearchVariant.withOutIllustration);
    }
  };

  const formatSelectedItemValue = (value: string): string =>
    value.split(constants.highLightElements.start).join('');

  const handleGetQuerySuggestion = async (queryText: string): Promise<void> => {
    // reset selectedItem whenever input value changes, as the previous selection list is now considered stale
    if (formattedSelectedItem) {
      setFormattedSelectedItem(undefined);
    }
    /**
     * When a query suggestion is selected, the value includes
     * HTML, so we check the value and clean it to display the
     * query text without HTML.
     */
    const isSelectedInputValue = queryText.includes(
      constants.highLightElements.start,
    );

    if (
      !isSelectedInputValue &&
      !formattedSelectedItem &&
      queryText.length >= constants.QUERY_SUGGESTION_MIN_LENGTH &&
      queryText.length <= constants.QUERY_SUGGESTION_MAX_LENGTH
    ) {
      debouncedSearchSuggestionsRef.current(queryText);
    }
  };

  const handleUpdateSearchValue = async (inputValue: string): Promise<void> => {
    if (inputValue.length >= constants.QUERY_SUGGESTION_MIN_LENGTH) {
      await handleGetQuerySuggestion(inputValue);
    }

    updateErrorText('');
    updateSearchValue(inputValue);
  };

  const handleSelectSuggestion = (suggestion: string): void => {
    const isSelectedInputValue = suggestion.includes(
      constants.highLightElements.start,
    );

    const value = isSelectedInputValue
      ? formatSelectedItemValue(suggestion)
      : suggestion;

    if (isSelectedInputValue) {
      setFormattedSelectedItem(value);
    }

    dispatch(searchValueActions.set(value));
    toggleSearchVariant();
    handleSearchSubmit(value);
  };

  const handleValidateSearchValue = async (
    event?: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event?.preventDefault();

    if (!searchValue) {
      updateErrorText(copy.errors.input);
    } else {
      handleSearchSubmit(searchValue);
    }
  };

  const handleSearchSubmit = (queryText: string): void => {
    dispatch(searchValueActions.set(queryText));
    updateQueryText(queryText);

    if (!isOnSearchPage) {
      navigate('/search');
    }
  };

  const handleTrackSearchSubmit = (): void => {
    const eventName = trackingDetails.deflectionSearchSubmit;

    logger.info(`${constants.LOG_PREFIX}::[${eventName}]`, { searchValue });
    trackEvent(eventName, { searchValue });
  };

  const renderQuerySuggestions = (resultText: string): ReactElement => (
    <Text as="p" dangerouslySetInnerHTML={{ __html: resultText }} />
  );

  const inputWidth =
    searchVariant === types.SupportSearchVariant.withIllustration
      ? '32rem'
      : '48.5rem';

  return (
    <Box
      as="section"
      className={cx(styles.searchContainer, {
        [styles.searchContainerWithIllustration]:
          searchVariant === types.SupportSearchVariant.withIllustration,
        [styles.searchContainerWithOutIllustration]:
          searchVariant === types.SupportSearchVariant.withOutIllustration,
      })}
      backgroundColor="colorBackgroundDecorative20Weakest"
    >
      <Box
        as="section"
        className={styles.searchInputContainer}
        width={inputWidth}
      >
        {isOnLandingPage && <WelcomeBanner />}

        <Heading as="h2" variant="heading30">
          {copy.heading}
        </Heading>

        <SearchInput
          id={constants.ids.supportSearch}
          value={searchValue}
          onChange={handleUpdateSearchValue}
          onTrackSearchSubmit={handleTrackSearchSubmit}
          onSubmit={handleValidateSearchValue}
          onSelectItem={handleSelectSuggestion}
          querySuggestionsItemRender={renderQuerySuggestions}
          querySuggestions={querySuggestionResults}
          marginBottom="space0"
          width={inputWidth}
          isLoading={isSearchResultsLoading}
          submitVariant={searchInputTypes.SearchInputSubmitVariant.inputButton}
          selectedItem={formattedSelectedItem}
          errorText={errorText}
          hasError={hasError}
        />
        {/* Tricky: The following code serves as a placeholder to keep the support search UI from jumping when error message appears */}
        {!hasError && <div className={styles.errorTextPlaceholder}></div>}
      </Box>
    </Box>
  );
};

export default SupportSearch;
